import { creating, exist } from '@campaign-portal/namespace/common/id';
import { TaskStatus } from '@campaign-portal/namespace/common/enums';
import { EdrExport } from '@campaign-portal/namespace/entities/edr/specs';
import { Filter } from '@campaign-portal/namespace/common/rpc.params';
import { FileInfo } from '@campaign-portal/namespace/common/fileInfo';
import { TagMode } from '@campaign-portal/components-library';

export interface EdrExportEntityConfig<ID = exist | creating> extends Partial<EdrExport<ID>> {
	id: ID;
	status: TaskStatus;
}

export class EdrExportEntity<ID = exist | creating> implements EdrExport<ID> {
	readonly id: ID;
	readonly name: string;
	readonly status: TaskStatus;
	readonly fields: string[];
	readonly filters: Filter[];
	readonly file?: FileInfo;
	readonly description?: string;
	readonly emails?: string;

	readonly creationDate?: string | null;
	readonly createdBy?: string | null;
	readonly lastUpdated?: string | null;
	readonly modifiedBy?: string | null;

	constructor(config: EdrExportEntityConfig<ID>) {
		this.id = config.id;
		this.name = config.name ?? '';
		this.status = config.status;
		this.fields = config.fields ?? [];
		this.filters = config.filters ?? [];
		this.file = config.file;
		this.description = config.description;
		this.emails = config.emails;
		this.creationDate = config.creationDate;
		this.createdBy = config.createdBy;
		this.lastUpdated = config.lastUpdated;
		this.modifiedBy = config.modifiedBy;
	}

	isWaiting(): boolean {
		return this.status === TaskStatus.WAITING;
	}

	isCancelled(): boolean {
		return this.status === TaskStatus.CANCELLED;
	}

	isFailed(): boolean {
		return this.status === TaskStatus.FAILED;
	}

	canCancel(): boolean {
		return this.isWaiting();
	}

	canRestart(): boolean {
		return this.isCancelled() || this.isFailed();
	}

	getStatusTagMode(): TagMode {
		switch (this.status) {
			case TaskStatus.WAITING:
				return 'info';
			case TaskStatus.SENT:
			case TaskStatus.READY:
				return 'success';
			case TaskStatus.CANCELLED:
				return 'gray-light';
			case TaskStatus.FAILED:
				return 'error';
			default:
				return 'gray';
		}
	}
}
